import {Component, OnInit} from '@angular/core';
import {UtilService} from '../../../../services/util/util.service';
import {ViewService} from '../../../../services/view/view.service';
import {SnackService} from '../../../../services/snack/snack.service';
import {ValidationService} from '../../../../services/validation/validation.service';
import {AbstractControlOptions, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidationErrors, Validators} from '@angular/forms';
import {RegistrationService, UserRegisterPost} from '../../../../generated/sales-terminal-client';
import {TranslateService} from '@ngx-translate/core';
import {RouteService} from '../../../../services/route/route.service';
import {LanguageService} from '../../../../services/language/language.service';
import {StorageService} from '../../../../services/storage/storage.service';
import {CentrePropertyService} from "../../../../services/centre-property/centre-property.service";

@Component({
  selector: 'app-registration-user-form',
  templateUrl: './registration-user-form.component.html',
  styleUrls: ['./registration-user-form.component.scss']
})
export class RegistrationUserFormComponent implements OnInit {
  static STORAGE_USER_REGISTRATION_FORM = 'userRegistrationForm.cache';
  public isLoading: boolean;
  public form: UntypedFormGroup;
  public showEmailExistsError: boolean;
  public oldEmail: string;

  constructor(
    private fb: UntypedFormBuilder,
    public viewService: ViewService,
    private registrationService: RegistrationService,
    private snackService: SnackService,
    public utilService: UtilService,
    public translateService: TranslateService,
    private routeService: RouteService,
    private languageService: LanguageService,
    private storageService: StorageService,
    private centrePropertyService: CentrePropertyService) {}

  get isSameEmail(): boolean {
    return this.oldEmail && this.oldEmail === this.form.get('email').value;
  }

  get termsAndConditionsLink(): string {
    return this.centrePropertyService.getValue('terms_and_conditions_link')?.replace('$lang', this.languageService.selectedLang);
  }

  ngOnInit(): void {
    this.form = this.fb.group({
      firstName: ['', [Validators.required]],
      lastName: ['', [Validators.required]],
      email: ['', [Validators.required, this.emailValidator.bind(this)]],
      phoneNr: [''],
      password: ['', [Validators.required, this.passwordValidator.bind(this)]],
      passwordRepeat: ['', [Validators.required, this.passwordValidator.bind(this)]],
      agreedToTermsAndConditions: [false, [this.termsAndConditionsValidator.bind(this)]] //
    }, {
      validators: [this.passwordMatchValidator.bind(this)]
    } as AbstractControlOptions);
    if (this.routeService.checkRoute(['registration', 'user', 'change'], this.routeService.url, 1)) {
      this.prefillFormFromCache();
      this.checkEmail();
    }
  }

  prefillFormFromCache() {
    const cache = this.storageService.get(RegistrationUserFormComponent.STORAGE_USER_REGISTRATION_FORM);
    if (cache) {
      const formData = JSON.parse(cache);
      this.form.setValue({
        firstName: formData.firstName || '',
        lastName: formData.lastName || '',
        email: formData.email || '',
        phoneNr: formData.phoneNr || '',
        password: formData.password || '',
        passwordRepeat: formData.passwordRepeat || '',
        agreedToTermsAndConditions: false
      });
      this.oldEmail = formData.email;
    }
  }

  checkEmail() {
    if (this.isSameEmail) {
      this.form.get('firstName').disable();
      this.form.get('lastName').disable();
      this.prefillFormFromCache();
    } else {
      this.form.get('firstName').enable();
      this.form.get('lastName').enable();
      this.form.get('password').setValue('');
      this.form.get('passwordRepeat').setValue('');
    }
  }

  cleanUpEmailValidation() {
    this.showEmailExistsError = false;
    this.form.get('email').setErrors(null);
  }

  submit(): void {
    if (!this.termsAndConditionsLink) {
      this.form.get('agreedToTermsAndConditions').setValue(true);
    }
    const formData = this.form.getRawValue();
    delete formData.passwordRepeat;
    delete formData.agreedToTermsAndConditions;
    this.registrationService.registerUser(formData as UserRegisterPost, this.languageService.selectedLang).subscribe({
      next: () => {
        this.snackService.showSuccess(this.translateService.instant('general.api_registration_user_success'))
        this.storeDataAndRedirect();
      },
      error: err => {
        if (err.error?.message && err.error.message === 'api_users_user_is_unverified') {
          this.storeDataAndRedirect();
        }
        if (err.error?.message && err.error.message === 'api_users_user_email_already_exists') {
          this.form.get('email').setErrors(['alreadyExists']);
          this.showEmailExistsError = true;
        } else {
          this.snackService.showBackendError(err);
        }
      }
    });
  }

  emailValidator(control: UntypedFormControl): ValidationErrors | null {
    return control.value && !ValidationService.validateEmail(control.value) ? {pattern: true} : null;
  }

  passwordValidator(control: UntypedFormControl): ValidationErrors | null {
    return control.value && !ValidationService.validatePassword(control.value) ? {pattern: true} : null;
  }

  passwordMatchValidator(group: UntypedFormGroup): ValidationErrors | null {
    const compare = group.get('passwordRepeat').value && group.get('password').value !== group.get('passwordRepeat').value ? {passwordMismatch: true} : null;
    if (compare != null) {
      group.get('passwordRepeat').setErrors(['passwordMismatch']);
    } else {
      if (!group.get('passwordRepeat').value) {
        group.get('passwordRepeat').setErrors([]);
      } else {
        group.get('passwordRepeat').setErrors(null);
      }
    }
    return compare;
  }

  termsAndConditionsValidator(control: UntypedFormControl): ValidationErrors | null {
    return this.termsAndConditionsLink && !control.value ? {termsAndConditionsNotAgreed: true} : null;
  }

  storeDataAndRedirect() {
    const formData = this.form.getRawValue();
    this.storageService.set(RegistrationUserFormComponent.STORAGE_USER_REGISTRATION_FORM, JSON.stringify(formData));
    this.routeService.navigate('/registration/user/resend');
  }

  openTermsAndConditions() {
    window.open(this.termsAndConditionsLink, "_blank");
  }
}
